'use client';

import React, { useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { Dialog, Input, PulseButton } from 'src/components';
import { useCreateListMutation, useGetListsQuery } from 'src/api/redux/lists/listsApi';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import useLists from 'src/hooks/lists/useLists';
import { useRouter } from 'next/navigation';

interface CreateListDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateListDialog = ({
  open,
  onClose,
}: CreateListDialogProps): React.ReactElement => {
  const [listName, setListName] = useState('');
  const [createList, { isLoading }] = useCreateListMutation();
  const { data: providerLists, isLoading: isListsLoading } = useGetListsQuery({}, { skip: !open });
  const { setSelectedList } = useLists();
  const router = useRouter();
  const log = useLogging();
  const { setNotification } = useNotification();

  const trimmedName = listName.trim();

  const nameAlreadyExists =
    trimmedName.length > 0 &&
    providerLists?.results?.some(
      (list: MedScout.List) =>
        list.name.toLowerCase() === trimmedName.toLowerCase()
    );

  const canSubmit = trimmedName.length > 0 && !nameAlreadyExists && !isLoading && !isListsLoading;

  const handleClose = () => {
    setListName('');
    onClose();
  };

  const handleCreate = async () => {
    if (!canSubmit) return;

    try {
      const createdList = await createList({ listName: trimmedName }).unwrap();
      const newListId = createdList.id.toString();

      log.event('List created', { name: trimmedName, source: 'createListDialog' });

      setSelectedList({
        id: newListId,
        name: trimmedName,
        path: `/lists/my/${newListId}`,
        owner: createdList.owner
          ? {
              id: createdList.owner.id,
              first_name: createdList.owner.first_name,
              last_name: createdList.owner.last_name,
            }
          : undefined,
      });

      setNotification({
        title: 'List created',
        message: `Created list "${trimmedName}"`,
        type: 'success',
      });

      handleClose();
      router.push(`/lists/my/${newListId}`);
    } catch (err: any) {
      log.exception('Error creating list', err);
      setNotification({
        title: 'Error creating list',
        message: err?.error_message || 'An error occurred while creating the list.',
        type: 'error',
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSubmit) {
      handleCreate();
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      aria-labelledby="create-list-dialog-title"
      data-testid="create-list-dialog"
    >
      <Dialog.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-sky-50">
              <Icon icon={faPlus} className="text-sky-600" />
            </div>
            <div
              className="text-lg font-medium"
              id="create-list-dialog-title"
            >
              Create List
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 transition-colors"
            aria-label="Close dialog"
            data-testid="create-list-dialog-close"
          >
            <Icon icon={faXmark} />
          </button>
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div className="p-4 flex flex-col gap-2">
          <label
            htmlFor="create-list-name-input"
            className="text-sm font-medium text-neutral-700"
          >
            List Name
          </label>
          <Input
            id="create-list-name-input"
            placeholder="Enter list name..."
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            error={nameAlreadyExists}
            className="rounded-full"
            aria-label="List name"
            data-testid="create-list-name-input"
          />
          {nameAlreadyExists && (
            <p
              className="text-xs text-red-500 px-1"
              data-testid="create-list-duplicate-error"
            >
              A list with this name already exists
            </p>
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <PulseButton
          variant="outline"
          onClick={handleClose}
          className="w-full rounded-full"
          aria-label="Cancel list creation"
          data-testid="create-list-dialog-cancel"
        >
          Cancel
        </PulseButton>
        <PulseButton
          variant="solid"
          color="primary"
          onClick={handleCreate}
          disabled={!canSubmit}
          loading={isLoading}
          className="w-full rounded-full"
          aria-label="Create list"
          data-testid="create-list-dialog-confirm"
        >
          Create
        </PulseButton>
      </Dialog.Actions>
    </Dialog>
  );
};

export { CreateListDialog as default };
